import { SearchRounded } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import useColors from "../../../../api/hooks/useColors";
import ToggleButton from "../../../../components/buttons/ToggleButton";
import ToggleGroup from "../../../../components/buttons/ToggleGroup";
import ClearFiltersButton from "../../../../components/filters/ClearFiltersButton";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { PageDisplayMode } from "../../../../utils/constants";
import SmartFilterButton from "../../environment/filters/smartFilters/SmartFilterButton";
import { icons } from "../../environment/icons/iconExport";

const AssetsOverviewActions = ({
  entityOptions,
  entitiesAreLoading,
  searchValue,
  setSearchValue,
  appliedFilters,
  setAppliedFilters,
  displayMode,
  setDisplayMode,
  setSearchParams,
  allCards,
}) => {
  const colors = useColors();

  return (
    <Box display="flex" justifyContent="space-between">
      <Box display="flex" gap={1} alignItems="center">
        <Autocomplete
          options={entityOptions}
          value={null}
          inputValue={searchValue}
          onInputChange={(event, newInputValue) => {
            setSearchValue(newInputValue);
          }}
          onChange={(event, newValue) => {
            if (newValue) {
              setSearchValue(newValue.label || "");
              setAppliedFilters({
                ...appliedFilters,
                searchText: newValue.value || newValue.label || "",
                searchDisplayName: newValue.label || "",
              });
            }
          }}
          onClear={() => {
            setSearchValue("");
            setAppliedFilters({
              ...appliedFilters,
              searchText: "",
            });
          }}
          clearOnBlur={false}
          selectOnFocus
          filterOptions={(options, state) => {
            // Custom filtering logic using state.inputValue for up-to-date filtering
            const searchTerm = state.inputValue.toLowerCase();
            const filteredOptions = options.filter(
              (option) =>
                option.label?.toLowerCase().includes(searchTerm) ||
                option.original_unique_id?.toLowerCase().includes(searchTerm) ||
                option.unique_id?.toLowerCase().includes(searchTerm)
            );
            return filteredOptions;
          }}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          noOptionsText={
            <Typography variant="body2">No matching options</Typography>
          }
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                "& .MuiInputBase-root": {
                  width: "305px",
                  height: "32px",
                  fontSize: "12px",
                  borderRadius: "10px",
                  backgroundColor: colors.common_white,
                  "& .MuiInputBase-root:hover": {
                    border: "1px solid " + colors.textfield_border,
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "10px",
                  borderColor: colors.textfield_border,
                },
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.textfield_border,
                    borderWidth: "1px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.textfield_border,
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: colors.contrast_text_dark,
                },
                width: "100%",
              }}
              placeholder="Search entities in assets"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <React.Fragment>
                    {entitiesAreLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {searchValue && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchValue("");
                          setAppliedFilters({
                            ...appliedFilters,
                            searchText: "",
                          });
                        }}
                        sx={{
                          padding: "2px",
                          color: colors.contrast_text_dark,
                          "&:hover": {
                            color: colors.primary_main,
                          },
                        }}
                      >
                        <SvgToIcon
                          IconLogo={icons.CloseIcon}
                          width="14px"
                          height="14px"
                        />
                      </IconButton>
                    )}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.value}>
              <Typography variant="body2">{option.label}</Typography>
            </Box>
          )}
        />
        <SmartFilterButton
          text="All Critical Assets"
          selected={!appliedFilters.active && !appliedFilters.updated}
          onClick={() =>
            setAppliedFilters({
              ...appliedFilters,
              active: false,
              updated: false,
            })
          }
          onRemove={() =>
            setAppliedFilters({
              ...appliedFilters,
              active: false,
              updated: false,
            })
          }
          disabled={false}
        />
        <SmartFilterButton
          text="Active assets"
          selected={appliedFilters.active}
          onClick={() =>
            setAppliedFilters({
              ...appliedFilters,
              updated: false,
              active: true,
            })
          }
          onRemove={() =>
            setAppliedFilters({
              ...appliedFilters,
              updated: false,
              active: false,
            })
          }
          disabled={false}
        />
        <SmartFilterButton
          text="Updated assets"
          selected={appliedFilters.updated}
          onClick={() =>
            setAppliedFilters({
              ...appliedFilters,
              updated: true,
              active: false,
            })
          }
          onRemove={() =>
            setAppliedFilters({
              ...appliedFilters,
              updated: false,
              active: false,
            })
          }
          disabled={false}
        />
        <ClearFiltersButton
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          onClearClick={() => {
            setSearchValue("");
          }}
        />
      </Box>

      <ToggleGroup>
        <ToggleButton
          iconSVG={icons.CardIcon}
          selected={displayMode === PageDisplayMode.CARD}
          onClick={() => {
            setDisplayMode(PageDisplayMode.CARD);
            setSearchParams({});
          }}
          disabled={false}
        >
          <Typography variant="overline">Cards View</Typography>
        </ToggleButton>
        <ToggleButton
          iconSVG={icons.TableIcon}
          selected={displayMode === PageDisplayMode.TABLE}
          onClick={() => {
            setDisplayMode(PageDisplayMode.TABLE);
            setSearchParams({ id: allCards[0].asset_id });
          }}
          disabled={false}
        >
          <Typography variant="overline">Table View</Typography>
        </ToggleButton>
      </ToggleGroup>
    </Box>
  );
};

export default AssetsOverviewActions;
